import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errorMessage, tokenFromHash } from "../lib/api";
import { Alert, Button, Field } from "../components/Form";
import { PageHeader } from "../components/FeatureCard";
import { t } from "../lib/i18n";

export function LoginPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState<"password" | "link" | "forgot">("password");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = params.get("next");
  const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : "/";

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      if (mode === "password") {
        await api("/api/auth/password-login", { method: "POST", json: { email: data.get("email"), password: data.get("password") } });
        window.location.assign(safeNext); // full load so every part of the site sees the new session
        return;
      }
      await api("/api/auth/login", { method: "POST", json: { email: data.get("email"), next: mode === "forgot" ? "/akaun" : next ?? undefined } });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const subtitle =
    mode === "password"
      ? t("Masukkan emel dan kata laluan anda.")
      : mode === "forgot"
        ? t("Masukkan emel anda. Kami hantar pautan untuk anda tetapkan kata laluan baharu.")
        : t("Kami hantar pautan log masuk sekali guna ke emel anda. Guna ini kali pertama atau jika terlupa kata laluan.");

  return (
    <div className="mx-auto max-w-md">
      <PageHeader title={t("Log masuk")} subtitle={subtitle} />
      {sent ? (
        <Alert kind="success">
          {mode === "forgot"
            ? t("Semak emel anda. Klik pautan untuk log masuk, kemudian tetapkan kata laluan baharu. Pautan sah selama 15 minit.")
            : t("Semak emel anda. Pautan sah selama 15 minit. Log masuk di peranti baharu akan menamatkan sesi lama.")}
        </Alert>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label={t("Emel")} name="email" type="email" required autoComplete="email" />
          {mode === "password" && <Field label={t("Kata laluan")} name="password" type="password" required maxLength={128} autoComplete="current-password" />}
          {error && <Alert kind="error">{error}</Alert>}
          <Button type="submit" busy={busy} className="w-full">{mode === "password" ? t("Log masuk") : mode === "forgot" ? t("Hantar pautan tetapan semula") : t("Hantar pautan")}</Button>
          {mode === "password" ? (
            <div className="space-y-2 text-center text-sm font-semibold text-brand">
              <button type="button" className="block w-full underline" onClick={() => { setMode("forgot"); setError(null); }}>{t("Lupa kata laluan?")}</button>
              <button type="button" className="block w-full underline" onClick={() => { setMode("link"); setError(null); }}>{t("Kali pertama? Hantar pautan ke emel")}</button>
            </div>
          ) : (
            <button type="button" className="block w-full text-center text-sm font-semibold text-brand underline" onClick={() => { setMode("password"); setError(null); }}>
              {t("Log masuk dengan kata laluan")}
            </button>
          )}
        </form>
      )}
    </div>
  );
}

export function LoginVerifyPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke would burn the single-use token
    ran.current = true;
    const token = tokenFromHash();
    if (!token) {
      setError(errorMessage(new Error()));
      return;
    }
    history.replaceState(null, "", window.location.pathname + window.location.search);
    api("/api/auth/verify", { method: "POST", json: { token } })
      .then(() => {
        const next = params.get("next");
        nav(next && next.startsWith("/") && !next.startsWith("//") ? next : "/", { replace: true });
      })
      .catch(async (e) => {
        // A used link opened again (e.g. chat app then browser): if this browser is already
        // signed in, carry on instead of showing an error.
        const me = await api<{ user: unknown }>("/api/me").catch(() => ({ user: null }));
        if (me.user) nav("/", { replace: true });
        else setError(errorMessage(e));
      });
  }, [nav, params]);

  return (
    <div className="mx-auto max-w-md">
      <PageHeader title={t("Log masuk")} subtitle={t("Mengesahkan pautan…")} />
      {error && <Alert kind="error">{error}</Alert>}
    </div>
  );
}
